import type { ColorPalette, ThemeHeadingPalette, ThemePalette, ThemeTypePalette, ThemeChartPalette, AssetPalette, GradientPalette, ShadowPalette, RadialPalette, } from "@bracket/ui/types";
import type { CSSProperties, } from "react";


export const headingFont = "Space Grotesk";
export const bodyFont = "Inter";
export const numericFont = "Space Mono";


export const basePalette: ColorPalette = {
	palette_type: "dark",

	white: "#DFE4DC",
	black: "#101510",

	background: "#0b0c0b", // actually specified: "#101510",
	background_on: "#DFE4DC",
	paragraph: "#aaaca7",
	subheading: "#8f918d",
	heading: "#f0f1ec",
	title: "#f0f1ec",
	primary: "#03FF8F",
	primary_variant: "#03FF8F",
	primary_on: "#141414",
	surface: "#101510",
	surface_on: "#eff1ef",
	surface_container: "#1f1f1f",
	surface_container_on: "#aaaca7",
}

export const palette: ThemePalette = {
	...basePalette,

	primary_container: "#13512D",
	primary_container_on: "#B2F1C0",
	primary_fixed: "#B2F1C0",
	primary_fixed_dim: "#97D5A5",
	outline: "#8B938A",
	outline_variant: "#414141",
	surface_hover: "#1e1e1e",

	surface_variant: "#414141",
	surface_variant_on: "#8F918D",

	surface_inverse: "#DFE4DC",
	surface_inverse_on: "#2D322D",

	surface_container_lowest: "#1a1a1a",
	surface_container_low: "#1f1f1f",
	surface_container_high: "#292929",
	surface_container_highest: "#1a1a1a",

	surface_container_bright: "#353A35",
	surface_light: basePalette.subheading,
	surface_light_on: basePalette.surface,

	surface_container_dim: "#101510",
	surface_dark: basePalette.surface,
	surface_dark_on: basePalette.surface_on,

	success: "#57FF0380",
	success_on: "#57FF03",
	success_container: "#57FF0320",

	error: "#E42F0380",
	error_on: "#E42F03",
	error_container: "#E42F0320",

	warning: "#FBBC2C80",
	warning_on: "#FBBC2C",
	warning_container: "#FBBC2C20",

	debug: "#FF000080",
	debug_on: "#F00",

	accent_blue: "#AFC6FF",
	accent_teal: "#A2CED9",
	accent_orange: "#FFB87C",
	accent_pink: "#FF7CA3",
	accent_purple: "#B890FF",
	accent_brand: "#03FF8F",

	tint_lightest: "#FFFFFF56",
	tint_lighter: "#FFFFFF28",
	tint_light: "#FFFFFF14",
	tine: "#00000014",
	tint_dark: "#00000014",
	tint_darker: "#00000014",
	tint_darkest: "#000000A4",

	swatches: "swatch_one",
	swatch_one: [
		"#09B166", // Light gray-green
		"#0C804D", // Light blue
		"#113C29", // Red
		"#293430", // Dark green
		"#343736", // Brown2CED9",]
	],

} as const;


export const assets: AssetPalette = {
	menu_background: "/assets/nav-menu-bkg.png",
	invest_background: "/assets/bracket-bg-invest.png",
	background_one: "/assets/stakeBg.png",
	HERO_BG: "/assets/bracket-bg-invest.png",
	TX_BKG_ONE: "/assets/dark/tx-glass-1.png",
	TX_GRAIN_ONE: "/assets/dark/tx-grain-1.png",
} as const;


export const fonts = {
	numeric: "Space Mono",
	body: "Inter",
	heading: "Space Grotesk",
	heading_variant: "Space Grotesk",
} as const;


export const paragraph: ThemeTypePalette = {
	xxsm: {
		font: fonts.body,
		size: "10px",
		lineHeight: "12px",
		weight: "400",
		color: palette.surface_on,
	},	
	xsm: {
		font: fonts.body,
		size: "12px",
		lineHeight: "16px",
		weight: "400",
		color: palette.surface_on,
	},
	sm: {
		font: fonts.body,
		size: "14px",
		lineHeight: "20px",
		weight: "400",
		color: palette.surface_on,
	},
	md: {
		font: fonts.body,
		size: "16px",
		lineHeight: "20px",
		weight: "400",
		color: palette.surface_on,
	},
	lg: {
		font: fonts.body,
		size: "18px",
		lineHeight: "24px",
		weight: "400",
		color: palette.surface_on,
	},
	xl: {
		font: fonts.body,
		size: "20px",
		lineHeight: "28px",
		weight: "400",
		color: palette.surface_on,
	},
	xxl: {
		font: fonts.body,
		size: "24px",
		lineHeight: "32px",
		weight: "400",
		color: palette.surface_on,
	},
} as const;


export const heading: ThemeHeadingPalette = {
	h1: {
		font: fonts.heading,
		size: "60px",
		lineHeight: "80px",
		weight: "500",
		color: palette.surface_on,
	},
	h2: {
		font: fonts.heading,
		size: "48px",
		lineHeight: "60px",
		weight: "500",
		color: palette.surface_on,
	},
	h3: {
		font: fonts.heading,
		size: "40px",
		lineHeight: "52px",
		weight: "500",
		color: palette.surface_on,
	},
	h4: {
		font: fonts.heading,
		size: "36px",
		lineHeight: "48px",
		weight: "500",
		color: palette.surface_on,
	},
	h5: {
		font: fonts.heading,
		size: "24px",
		lineHeight: "32px",
		weight: "500",
		color: palette.surface_on,
	},
	h6: {
		font: fonts.heading,
		size: "20px",
		lineHeight: "28px",
		weight: "500",
		color: palette.surface_on,
	},
} as const;


export const numeric: ThemeTypePalette = {
	xxsm: {
		font: fonts.numeric,
		size: "10px",
		lineHeight: "12px",
		weight: "400",
		color: palette.surface_on,
	},	
	xsm: {
		font: fonts.numeric,
		size: "12px",
		lineHeight: "16px",
		weight: "400",
		color: palette.surface_on,
	},
	sm: {
		font: fonts.numeric,
		size: "14px",
		lineHeight: "20px",
		weight: "400",
		color: palette.surface_on,
	},
	md: {
		font: fonts.numeric,
		size: "16px",
		lineHeight: "20px",
		weight: "400",
		color: palette.surface_on,
	},
	lg: {
		font: fonts.numeric,
		size: "18px",
		lineHeight: "24px",
		weight: "400",
		color: palette.surface_on,
	},
	xl: {
		font: fonts.numeric,
		size: "20px",
		lineHeight: "28px",
		weight: "400",
		color: palette.surface_on,
	},
	xxl: {
		font: fonts.numeric,
		size: "24px",
		lineHeight: "32px",
		weight: "400",
		color: palette.surface_on,
	},
} as const;


export const chart: ThemeChartPalette = {
	stroke: palette.surface_variant,
	stroke_width: 10,
	axis: {
		color: palette.outline,
		swatch: "swatch_one",
	}
}

export const gradients: GradientPalette = {
	primary: "linear-gradient(180deg, rgba(20, 23, 21, 0.95) 0%, rgba(11, 19, 16, 0.98) 50%, rgba(18, 50, 36, 1) 100%)",
	secondary: "linear-gradient(180deg, #03ff8f00 0%, #1e1e1e 76.96%)",
	tertiary: "linear-gradient(180deg, #03FF8F40 0%, #0b6430 100%)",
	quaternary: "linear-gradient(180deg, #03FF8F40 0%, #0b6430 100%)",
	quinary: "linear-gradient(180deg, #03FF8F40 0%, #0b6430 100%)",
	senary: "linear-gradient(180deg, #03FF8F40 0%, #0b6430 100%)",
	navpanel: `linear-gradient(180deg, ${palette.surface_container_lowest} 0%, ${palette.surface_container_lowest} 100%)`,
} as const;


export const shadows: ShadowPalette = {
	primary: "0px 0px 10px 0px rgba(0, 255, 143, 0.0)",
	secondary: "0px 0px 10px 0px rgba(0, 255, 143, 0.0)",
	tertiary: "0px 0px 10px 0px rgba(0, 255, 143, 0.0)",
	quaternary: "0px 0px 10px 0px rgba(0, 255, 143, 0.0)",
	quinary: "0px 0px 10px 0px rgba(0, 255, 143, 0.0)",
	senary: "0px 0px 10px 0px rgba(0, 255, 143, 0.0)",
	navpanel: "0px 0px 10px 0px rgba(0, 255, 143, 0.0)",
} as const;

export const radials: RadialPalette = {
	xxsm: "3px", // 2 is to small to even notice
	xsm: "4px",
	sm: "8px",
	md: "16px",
	lg: "24px",
	xl: "32px",
	xxl: "64px",
} as const;

export const radialPalette: RadialPalette = {
	xxsm: "3px", // 2 is to small to even notice
	xsm: "4px",
	sm: "8px",
	md: "16px",
	lg: "24px",
	xl: "32px",
	xxl: "64px",
} as const;

export const uniques: Record<string, CSSProperties> = {
	DARK_STAKE_STATUS_BG: { background: "/assets/stakeBg.png", },
};
